import { useCallback, useState } from 'react';

/**
 * @typedef {'design' | 'output'} PageMode
 * @typedef {'new' | 'original' | 'compare'} ResultViewMode
 */

/**
 * @returns {{
 *   pageMode: PageMode;
 *   setPageMode: import('react').Dispatch<import('react').SetStateAction<PageMode>>;
 *   resultViewMode: ResultViewMode;
 *   setResultViewMode: import('react').Dispatch<import('react').SetStateAction<ResultViewMode>>;
 *   currentProject: Record<string, unknown> | null;
 *   setCurrentProject: import('react').Dispatch<import('react').SetStateAction<Record<string, unknown> | null>>;
 *   enterOutputMode: (project: Record<string, unknown>) => void;
 *   enterEditMode: () => void;
 *   resetToDesign: () => void;
 *   isEditMode: boolean;
 * }}
 */
export function useDesignWorkspaceMode() {
  const [pageMode, setPageMode] = useState(/** @type {PageMode} */ ('design'));
  const [resultViewMode, setResultViewMode] = useState(/** @type {ResultViewMode} */ ('new'));
  const [currentProject, setCurrentProject] = useState(
    /** @type {Record<string, unknown> | null} */ (null),
  );

  const enterOutputMode = useCallback((project) => {
    setCurrentProject(project);
    setPageMode('output');
    setResultViewMode('new');
  }, []);

  const enterEditMode = useCallback(() => {
    setPageMode('design');
  }, []);

  const resetToDesign = useCallback(() => {
    setPageMode('design');
    setResultViewMode('new');
    setCurrentProject(null);
  }, []);

  const isEditMode = pageMode === 'design' && currentProject != null;

  return {
    pageMode,
    setPageMode,
    resultViewMode,
    setResultViewMode,
    currentProject,
    setCurrentProject,
    enterOutputMode,
    enterEditMode,
    resetToDesign,
    isEditMode,
  };
}
